import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Backdrop, CircularProgress } from "@mui/material";
import useAlert from "../Reusables/UseAlert";
import {
  fetchInventoryByCategory,
  fetchInventoryFabric,
  fetchInventoryLiftStyle,
  getProductTypesAction,
} from "../../../redux/actions/orderActions";
import { useDebouncedCallback } from "../../../functions/debounceHook";
import {
  createMeasurement,
  fetchMeasurementData,
  updateMeasurement,
} from "../../../redux/actions/measurementActions";
import { dealerFetchSettingsAction } from "../../../redux/actions/userSettings";
import UserSettingsModal from "../../AttributeSettings/UseSettingsModal";
import CostMetricsCard from "../Reusables/CostAndSqft";
import DynamicTable from "../Reusables/DynamicTable";
import CustomKeyboard from "../CustomKeyboard";
import { formatLabel } from "../Reusables/formatlabel";

const CreateOrderForManufacturer = () => {
  const { dealerInfo } = useSelector((state) => state.dealerLogin);
  const inventoryByCategory = useSelector((state) => state.inventoryByCategory);
  const { loading: IBCLoading, error, inventory } = inventoryByCategory;
  const { settings: settingsData } = useSelector((state) => state.userSettings);
  const {
    fabrics,
    loading: fabricsLoading,
    error: FabricsError,
  } = useSelector((state) => state.inventoryFabrics);
  const {
    liftStyleInventory,
    loading: liftstyleLoading,
    error: liftstyleError,
  } = useSelector((state) => state.liftStyleInventory);
  const { productTypes } = useSelector((state) => state.productTypes);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showAlert, alertElement } = useAlert();
  const tableContainerRef = useRef(null);
  const inputRefs = useRef([]);

  const [productType, setProductType] = useState("");
  const [category, setCategory] = useState("");
  const [tableData, setTableData] = useState([
    {
      width: "",
      height: "",
      sqft: 0,
      remark: "",
      location: "",
      liftStyle: "",
      productMaterial: "",
      productHeadrail: "",
      sleeveOption: "",
      isEditing: false,
    },
  ]);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [totalSqft, setTotalSqft] = useState(0);
  const [totalCost, setTotalCost] = useState(0);
  const [submitLoader, setSubmitLoader] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(null);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(true);
  const [measurementId, setMeasurementId] = useState(null);
  const [isMeasurementCreated, setIsMeasurementCreated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tag, setTag] = useState("");
  const tableDataRef = useRef(tableData);
  tableDataRef.current = tableData;

  // Validate tableData for submission (strict for final order submission)
  const isValidForSubmission = () => {
    if (!productType) return false;
    if (totalCost <= 0) return false;
    return tableData.some(
      (row) =>
        row.width &&
        row.width.trim() !== "" &&
        row.height &&
        row.height.trim() !== "" &&
        row.productHeadrail &&
        row.productHeadrail.trim() !== "" &&
        row.productMaterial &&
        row.productMaterial.trim() !== "" &&
        row.liftStyle &&
        row.liftStyle.trim() !== ""
    );
  };

  // Validate tableData for debounced updates 
  const isValidForDebounce = () => {
    if (!productType) return false;
   
    return tableData.some(
      (row) =>
        (row.width && row.width.trim() !== "") ||
        (row.height && row.height.trim() !== "") ||
        (row.productHeadrail && row.productHeadrail.trim() !== "") ||
        (row.productMaterial && row.productMaterial.trim() !== "") ||
        (row.liftStyle && row.liftStyle.trim() !== "")
    );
  };

  useEffect(() => {
    if (productType) {
      dispatch(fetchInventoryByCategory(productType, dealerInfo.token));
      dispatch(fetchInventoryFabric(productType, dealerInfo.token));
      dispatch(fetchInventoryLiftStyle(productType, dealerInfo.token));
    }
  }, [dispatch, productType]);

  useEffect(() => {
    if (dealerInfo?.token) {
      dispatch(getProductTypesAction(dealerInfo.token));
    }
  }, [dispatch, dealerInfo]);

  useEffect(() => {
    const handleResize = () => {
      if (window.matchMedia("(max-width: 1000px)").matches) {
        setIsReadOnly(true);
        setIsKeyboardVisible(true);
      } else {
        setIsReadOnly(false);
        setIsKeyboardVisible(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (focusedIndex !== null && inputRefs.current[focusedIndex.row]) {
      const input = inputRefs.current[focusedIndex.row][focusedIndex.field];
      if (input) {
        input.focus();
        input.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }, [focusedIndex]);

  const debouncedUpdateMeasurement = useDebouncedCallback(async () => {
    if (measurementId && hasUnsavedChanges && isValidForDebounce()) {
      try {
        await updateMeasurement(
          measurementId,
          productType,
          tag,
          tableDataRef.current,
          dealerInfo.token,
          totalSqft,
          undefined, // No orderStatus for debounced updates
          showAlert
        );
        setHasUnsavedChanges(false);
        fetchMeasurementData(
          measurementId,
          dealerInfo.token,
          setTableData,
          tableData,
          hasUnsavedChanges,
          setTotalSqft,
          setTotalCost
        );
      } catch (error) {
        console.error("Error in debounced update:", error);
        showAlert("Error saving changes. Please try again.", "error");
      }
    }
  }, 5000);

  useEffect(() => {
    if (measurementId) {
      const intervalId = setInterval(() => {
        debouncedUpdateMeasurement();
      }, 7000);
      return () => clearInterval(intervalId);
    }
  }, [debouncedUpdateMeasurement, measurementId]);

  useEffect(() => {
    const scrollToBottom = () => {
      const container = tableContainerRef.current;
      if (container) {
        setTimeout(() => {
          container.scrollTop = container.scrollHeight;
        }, 100);
      }
    };
    scrollToBottom();
  }, [tableData]);

  const handleInputChange = (index, field, value) => {
    const newData = [...tableData];
    newData[index][field] = value;
    if (field === "productHeadrail") {
      newData[index]["sleeveOption"] = "";
    }
    setTableData(newData);
    setHasUnsavedChanges(true);

    const isCurrentRowComplete =
      newData[index].width &&
      newData[index].width.trim() !== "" &&
      newData[index].height &&
      newData[index].height.trim() !== "" &&
      newData[index].liftStyle &&
      newData[index].liftStyle.trim() !== "" &&
      newData[index].productMaterial &&
      newData[index].productMaterial.trim() !== "" &&
      newData[index].productHeadrail &&
      newData[index].productHeadrail.trim() !== "";

    if (isCurrentRowComplete) {
      debouncedUpdateMeasurement();
    }

    if (isCurrentRowComplete && index === newData.length - 1) {
      setTableData([
        ...newData,
        {
          width: "",
          height: "",
          sqft: 0,
          remark: "",
          location: "",
          liftStyle: newData[index].liftStyle || "",
          productMaterial: newData[index].productMaterial || "",
          productHeadrail: newData[index].productHeadrail || "",
          sleeveOption: newData[index].sleeveOption || "",
          isEditing: false,
        },
      ]);
    }

    if (
      newData.length > 1 &&
      index === newData.length - 1 &&
      (!newData[index].width || newData[index].width.trim() === "") &&
      (!newData[index].height || newData[index].height.trim() === "") &&
      (!newData[index].liftStyle || newData[index].liftStyle.trim() === "") &&
      (!newData[index].productMaterial ||
        newData[index].productMaterial.trim() === "") &&
      (!newData[index].productHeadrail ||
        newData[index].productHeadrail.trim() === "")
    ) {
      setTableData(newData.slice(0, -1));
    }
  };

  const handleKeyPress = (key) => {
    if (focusedIndex !== null) {
      const { row, field } = focusedIndex;
      const currentValue = tableData[row][field];
      const keyString = String(key);

      if (keyString.includes("/")) {
        const parts = currentValue.split(" ");
        if (parts.length > 1) {
          parts[parts.length - 1] = keyString;
        } else {
          parts.push(keyString);
        }
        const newValue = parts.join(" ");
        handleInputChange(row, field, newValue);
      } else {
        const newValue = currentValue + keyString;
        handleInputChange(row, field, newValue);
      }
    }
  };

  const handleNext = () => {
    if (focusedIndex !== null) {
      const { row, field } = focusedIndex;
      if (field === "width") {
        setFocusedIndex({ row, field: "height" });
      } else if (field === "height") {
        setFocusedIndex({ row, field: "productHeadrail" });
      } else if (field === "productHeadrail") {
        setFocusedIndex({ row, field: "sleeveOption" });
      } else if (field === "sleeveOption") {
        setFocusedIndex({ row, field: "productMaterial" });
      } else if (field === "productMaterial") {
        setFocusedIndex({ row, field: "liftStyle" });
      } else if (field === "liftStyle" && row < tableData.length - 1) {
        setFocusedIndex({ row: row + 1, field: "width" });
      }
    }
  };

  const handlePrevious = () => {
    if (focusedIndex !== null) {
      const { row, field } = focusedIndex;
      if (field === "liftStyle") {
        setFocusedIndex({ row, field: "productMaterial" });
      } else if (field === "productMaterial") {
        setFocusedIndex({ row, field: "sleeveOption" });
      } else if (field === "sleeveOption") {
        setFocusedIndex({ row, field: "productHeadrail" });
      } else if (field === "productHeadrail") {
        setFocusedIndex({ row, field: "height" });
      } else if (field === "height") {
        setFocusedIndex({ row, field: "width" });
      } else if (field === "width" && row > 0) {
        setFocusedIndex({ row: row - 1, field: "liftStyle" });
      }
    }
  };

  const handleEnter = () => {
    if (focusedIndex !== null) {
      const { row } = focusedIndex;
      if (row < tableData.length - 1) {
        setFocusedIndex({ row: row + 1, field: "width" });
      } else {
        setTableData([
          ...tableData,
          {
            width: "",
            height: "",
            sqft: 0,
            remark: "",
            location: "",
            liftStyle: "",
            productMaterial: "",
            productHeadrail: "",
            sleeveOption: "",
            isEditing: false,
          },
        ]);
        setFocusedIndex({ row: row + 1, field: "width" });
      }
    }
  };

  const handleClear = () => {
    if (focusedIndex !== null) {
      const { row, field } = focusedIndex;
      handleInputChange(row, field, "");
    }
  };

  const handleFocus = (row, field) => {
    if (field === "width" || field === "height") {
      setFocusedIndex({ row, field });
      if (window.matchMedia("(max-width: 1000px)").matches) {
        setIsKeyboardVisible(true);
      }
    } else {
      setIsKeyboardVisible(false);
    }
  };

  const handleSubmitOrder = async () => {
    if (!isValidForSubmission()) {
      showAlert(
        "Please ensure at least one row is complete with all required fields and total cost is greater than zero.",
        "error"
      );
      return;
    }

    if (measurementId) {
      setSubmitLoader(true);
      try {
        const orderStatus = "confirmed";
        await updateMeasurement(
          measurementId,
          productType,
          tag,
          tableData,
          dealerInfo.token,
          totalSqft,
          orderStatus,
          showAlert
        );
        showAlert("Order successfully sent to manufacturer.", "success");
        navigate(`/order/list`);
      } catch (error) {
        console.error(
          "Error updating measurement:",
          error.response ? error.response.data : error.message
        );
        showAlert(
          error.response?.data?.message ||
            "Error updating measurement. Please try again.",
          "error"
        );
      } finally {
        setSubmitLoader(false);
      }
    }
  };

  useEffect(() => {
    if (productType && !isMeasurementCreated) {
      createMeasurement(
        productType,
        tableData,
        dealerInfo._id,
        dealerInfo.token,
        setMeasurementId,
        setTotalSqft,
        setIsMeasurementCreated,
        setLoading,
        loading
      );
    }
  }, [productType]);

  useEffect(() => {
    dispatch(dealerFetchSettingsAction(dealerInfo.token));
  }, [dispatch]);

  const measurementColumns = [
    {
      header: (
        <>
          Width <span style={{ color: "red" }}>*</span>
        </>
      ),
      key: "width",
      inputClass: "",
      width: "100px",
      focusable: true,
    },
    {
      header: (
        <>
          Height <span style={{ color: "red" }}>*</span>
        </>
      ),
      key: "height",
      inputClass: "",
      width: "100px",
      focusable: true,
    },
    {
      header: (
        <>
          Headrail <span style={{ color: "red" }}>*</span>
        </>
      ),
      key: "productHeadrail",
      inputClass: "",
      width: "150px",
      focusable: true,
    },
    {
      header: "Sleeve Option",
      key: "sleeveOption",
      inputClass: "",
      width: "150px",
      focusable: true,
    },
    {
      header: (
        <>
          Fabric <span style={{ color: "red" }}>*</span>
        </>
      ),
      key: "productMaterial",
      inputClass: "",
      width: "150px",
      focusable: true,
    },
    {
      header: (
        <>
          LiftStyle <span style={{ color: "red" }}>*</span>
        </>
      ),
      key: "liftStyle",
      inputClass: "",
      width: "150px",
      focusable: true,
    },
    {
      header: "Sqft",
      key: "sqft",
      inputClass: "",
      width: "70px",
      readOnly: true,
    },
    {
      header: "Cost",
      key: "payment",
      inputClass: "",
      width: "70px",
      readOnly: true,
    },
    ...(settingsData?.showLocation
      ? [{ header: "Location", key: "location", inputClass: "" }]
      : []),
    ...(settingsData?.showRemark
      ? [{ header: "Remarks", key: "remark", inputClass: "" }]
      : []),
    ...(settingsData?.showMountLift
      ? [{ header: "Lift", key: "lift", inputClass: "" }]
      : []),
    ...(settingsData?.showControl
      ? [{ header: "Control", key: "control", inputClass: "" }]
      : []),
    ...(settingsData?.showTile
      ? [{ header: "Tile", key: "tile", inputClass: "" }]
      : []),
  ];

  const tableContainerStyles = {
    maxHeight: "100%",
    overflowY: "auto",
    position: "relative",
    transition: "padding-bottom 0.3s ease",
    paddingBottom: isKeyboardVisible ? "100px" : "0px",
  };

  const handleAddRow = (index) => {
    const newRow = {
      width: "",
      height: "",
      sqft: 0,
      remark: "",
      location: "",
      liftStyle: "",
      productMaterial: "",
      productHeadrail: "",
      sleeveOption: "",
      isEditing: false,
    };
    const updatedData = [
      ...tableData.slice(0, index + 1),
      newRow,
      ...tableData.slice(index + 1),
    ];
    setTableData(updatedData);
    setHasUnsavedChanges(true);
  };

  const handleDeleteRow = (index) => {
    const updatedData = tableData.filter((_, i) => i !== index);
    setTableData(updatedData);
    setHasUnsavedChanges(true);
  };

  const handleSettingsClick = () => {
    setShowSettingsModal(true);
  };

  const handleCloseModal = () => {
    setShowSettingsModal(false);
  };

  const handleCloseKeyboard = () => {
    setIsKeyboardVisible(false);
  };

  return (
    <div className="container mt-5 mb-4">
      {alertElement}
      <div className="row">
        <div className="col-lg-10">
          <span
            className="badge"
            style={{
              backgroundColor: "#007bff",
              color: "#fff",
              fontWeight: "bold",
              padding: "8px 16px",
              borderRadius: "16px",
              fontSize: "15px",
            }}
          >
            Send Order To Manufacturer
          </span>
        </div>
        <div
          className="col-lg-2 d-flex justify-content-between align-items-center"
          style={{ marginTop: "20px", marginBottom: "20px" }}
        >
          <button
            type="button"
            className="btn btn-success me-2 d-flex align-items-center"
            onClick={handleSubmitOrder}
            disabled={submitLoader || hasUnsavedChanges || !isValidForSubmission()}
            style={{
              fontSize: "1rem",
              padding: "10px 16px",
              borderRadius: "0.375rem",
              minWidth: "150px",
              textAlign: "center",
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
              cursor:
                submitLoader || hasUnsavedChanges || !isValidForSubmission()
                  ? "not-allowed"
                  : "pointer",
            }}
            onMouseEnter={(e) => {
              if (!submitLoader && !hasUnsavedChanges && isValidForSubmission()) {
                e.currentTarget.style.transform = "scale(1.05)";
                e.currentTarget.style.boxShadow =
                  "0 4px 15px rgba(0, 123, 255, 0.5)";
              }
            }}
            onMouseLeave={(e) => {
              if (!submitLoader && !hasUnsavedChanges && isValidForSubmission()) {
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.boxShadow = "none";
              }
            }}
          >
            {submitLoader ? (
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>
            ) : (
              <>
                <i className="bi bi-file-earmark-text me-1"></i>
                Send to Manufacturer
              </>
            )}
          </button>
          <button
            onClick={handleSettingsClick}
            className="btn btn-secondary d-flex align-items-center"
            style={{
              fontSize: "0.9rem",
              padding: "8px 12px",
              borderRadius: "0.375rem",
            }}
          >
            <i className="bi bi-gear me-1"></i>
            Settings
          </button>
        </div>
        {showSettingsModal && (
          <UserSettingsModal
            show={showSettingsModal}
            onClose={handleCloseModal}
          />
        )}
      </div>

      <div className="row mb-3">
        <div className="col-lg-4">
          <label className="form-label">Product Type</label>
          <div className="input-group">
            <span className="input-group-text">
              <i className="bi bi-box"></i>
            </span>
            <select
              className="form-select"
              value={productType}
              onChange={(e) => setProductType(e.target.value)}
              disabled={submitLoader}
            >
              <option value="">Select Product Type</option>
              {loading && <option disabled>Loading...</option>}
              {error && <option disabled>Error: {error}</option>}
              {productTypes.map((option) => (
                <option key={option._id} value={option.name}>
                  {formatLabel(option.name)}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="col-lg-4">
          <label className="form-label">Tags</label>
          <div className="input-group">
            <span className="input-group-text">
              <i className="bi bi-tag"></i>
            </span>
            <input
              type="text"
              className="form-control"
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              placeholder="Enter tags separated by commas"
              disabled={submitLoader}
            />
          </div>
        </div>
      </div>

      <CostMetricsCard
        totalSqft={totalSqft}
        totalCost={totalCost}
        header={"Estimated Cost"}
      />

      <div
        className="table-responsive"
        style={tableContainerStyles}
        ref={tableContainerRef}
      >
        <div style={{ overflowX: "auto" }}>
          {measurementId && (
            <DynamicTable
              tableData={tableData}
              handleInputChange={handleInputChange}
              handleAddRow={handleAddRow}
              handleDeleteRow={handleDeleteRow}
              columns={measurementColumns}
              handleFocus={handleFocus}
              inputRefs={inputRefs}
              isReadOnly={isReadOnly}
              liftStyleInventory={liftStyleInventory}
              fabrics={fabrics}
              inventory={inventory}
            />
          )}
        </div>
      </div>

      {isKeyboardVisible && (
        <CustomKeyboard
          onKeyPress={handleKeyPress}
          onNext={handleNext}
          onPrevious={handlePrevious}
          onEnter={handleEnter}
          onClear={handleClear}
          onClose={handleCloseKeyboard}
        />
      )}
      {(loading || submitLoader) && (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={loading || submitLoader}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      <div className="d-md-none" style={{ height: "50vh" }}></div>
    </div>
  );
};

export default CreateOrderForManufacturer;